import { findFirstNonEmptyString } from "@product/scmp-sdk";
import { useSyncedRef } from "@react-hookz/web";
import type { FunctionComponent } from "react";
import { useEffect } from "react";
import { graphql, readInlineData, useFragment } from "react-relay";

import type { notificationArticle$key } from "scmp-app/queries/__generated__/notificationArticle.graphql";
import type { notificationSection$key } from "scmp-app/queries/__generated__/notificationSection.graphql";

import { Label, NotificationContainer, ReadButton, StyledEntityLink, Title } from "./styles";

export type Props = {
  onClose?: () => void;
  onImpression?: () => void;
  onRead?: () => void;
  reference: notificationArticle$key;
  variant:
    | {
        type: "breaking";
      }
    | {
        reference: notificationSection$key;
        type: "section";
      };
};

export type Variant = Props["variant"];
export type Type = Props["variant"]["type"];

export const Notification: FunctionComponent<Props> = ({
  onClose,
  onImpression,
  onRead,
  reference: reference_,
  variant,
}) => {
  const article = useFragment(
    graphql`
      fragment notificationArticle on Article {
        ...entityLink
        headline
        socialHeadline
        flag
      }
    `,
    reference_,
  );

  const latestOnImpression = useSyncedRef(onImpression);
  useEffect(() => {
    latestOnImpression?.current?.();
  }, [article, latestOnImpression]);

  const computeViewModel = (variant: Variant) => {
    const title = findFirstNonEmptyString(article.socialHeadline, article.headline);

    switch (variant.type) {
      case "breaking":
        return {
          label: `${article.flag?.toUpperCase() ?? "BREAKING"} NEWS:`,
          title,
        };
      case "section":
        const section = readInlineData<notificationSection$key>(
          graphql`
            fragment notificationSection on Section @inline {
              ... on Section {
                name
              }
            }
          `,
          variant.reference,
        );

        return {
          label: section.name,
          title,
        };
    }
  };
  const vm = computeViewModel(variant);

  return (
    <NotificationContainer $type={variant.type} onClose={onClose}>
      <StyledEntityLink reference={article} onClick={onRead}>
        <Label>{vm.label}</Label>
        <Title>{vm.title}</Title>
        <ReadButton noStyle>READ</ReadButton>
      </StyledEntityLink>
    </NotificationContainer>
  );
};

Notification.displayName = "Notification";
